/**
 * The qualified organization registry.
 *
 * An organization is registered once and qualified once. Each of the five
 * elements — qualifying basis, evidence, oversight method, point of contact,
 * activity types — gets a named check (who, when, against which criteria), and
 * "Qualify" is refused until all five have one. Every Verify organization reads
 * the same list, so a county or another CBO never re-qualifies a site member by
 * member.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Circle, Copy, ExternalLink, Plus, Printer, QrCode, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { PageState } from '../weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  ACTIVITY_TYPES,
  DISCOVERY,
  OVERSIGHT_METHODS,
  QUALIFYING_BASES,
  SITE_ELEMENTS,
  type Site,
  type SiteElement,
  VerifyApiError,
  verifyApi,
} from './api';
import {
  ErrorNote,
  Field,
  NativeSelect,
  SiteStatusBadge,
  activityLabel,
  elementLabel,
  errMessage,
  fmtDate,
  humanize,
  inputClass,
} from './shared';

export function VerifyRegistry() {
  const { t } = useAppTranslation();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSites((await verifyApi.sites()).sites);
    } catch (e) {
      setError(errMessage(e, t('verify.registry.load_failed', 'Could not load the registry.')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = sites.find((s) => s.id === openId) || null;
  const replace = (site: Site) => setSites((prev) => prev.map((s) => (s.id === site.id ? site : s)));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-ink-500">
          {t(
            'verify.registry.intro',
            'Organizations where qualifying activity happens. Qualify each one once; every Verify organization can read the result.',
          )}
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('verify.refresh', 'Refresh')}
          </Button>
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('verify.registry.add', 'Add organization')}
          </Button>
        </div>
      </div>

      <ErrorNote message={error} />

      {loading && sites.length === 0 ? (
        <PageState variant="loading" />
      ) : sites.length === 0 ? (
        <PageState
          variant="empty"
          title={t('verify.registry.empty_title', 'No organizations yet')}
          message={t('verify.registry.empty', 'Add the first organization where members volunteer, train or study.')}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-v2border text-left text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">{t('verify.registry.col_org', 'Organization')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.registry.col_status', 'Status')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.registry.col_checks', 'Checks')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.registry.col_activities', 'Activities')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.registry.col_contact', 'Point of contact')}</th>
                    <th className="px-4 py-2 font-medium">{t('verify.registry.col_registered', 'Registered by')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map((s) => (
                    <tr
                      key={s.id}
                      className="cursor-pointer border-b border-v2border last:border-0 hover:bg-paper-2"
                      onClick={() => setOpenId(s.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-ink">{s.name}</div>
                        <div className="text-xs text-ink-500">
                          {[s.city, s.state].filter(Boolean).join(', ') ||
                            (s.host_org_name ? t('verify.registry.on_weavein', 'On WeaveIn') : '—')}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <SiteStatusBadge status={s.status} t={t} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-700">
                        {SITE_ELEMENTS.length - s.missing_checks.length}/{SITE_ELEMENTS.length}
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        {s.activity_types.map((a) => activityLabel(t, a)).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-ink-700">{s.contact_name || '—'}</td>
                      <td className="px-4 py-3 text-ink-500">{s.registered_by_org_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <AddSiteDialog
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={(site) => {
          setSites((prev) => [site, ...prev]);
          setAdding(false);
          setOpenId(site.id);
        }}
      />

      <Sheet open={!!open} onOpenChange={(v) => !v && setOpenId(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
          {open && <SiteDetail site={open} onChange={replace} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add organization
// ---------------------------------------------------------------------------

const emptySite = {
  name: '',
  host_cbo_id: '' as string,
  street_address: '',
  city: '',
  state: '',
  zip_code: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  qualifying_basis: '',
  qualifying_evidence: '',
  oversight_method: '',
  activity_types: [] as string[],
  discovered_via: 'outreach',
};

function AddSiteDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (site: Site) => void;
}) {
  const { t } = useAppTranslation();
  const [form, setForm] = useState({ ...emptySite });
  const [orgQuery, setOrgQuery] = useState('');
  const [orgs, setOrgs] = useState<{ id: number; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setForm({ ...emptySite });
      setOrgQuery('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      verifyApi
        .organizations(orgQuery)
        .then((r) => setOrgs(r.organizations))
        .catch(() => setOrgs([]));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgQuery, open]);

  const set = (k: keyof typeof emptySite, v: string | string[]) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (a: string) =>
    set('activity_types', form.activity_types.includes(a) ? form.activity_types.filter((x) => x !== a) : [...form.activity_types, a]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        host_cbo_id: form.host_cbo_id ? Number(form.host_cbo_id) : null,
        activity_types: form.activity_types,
        discovered_via: form.discovered_via,
      };
      (
        [
          'street_address',
          'city',
          'state',
          'zip_code',
          'contact_name',
          'contact_phone',
          'contact_email',
          'qualifying_basis',
          'qualifying_evidence',
          'oversight_method',
        ] as const
      ).forEach((k) => {
        const v = form[k].trim();
        if (v) body[k] = v;
      });
      onCreated(await verifyApi.createSite(body));
    } catch (e) {
      setError(errMessage(e, t('verify.registry.create_failed', 'Could not add the organization.')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('verify.registry.add_title', 'Add an organization to the registry')}</DialogTitle>
          <DialogDescription>
            {t(
              'verify.registry.add_desc',
              'Adding is not qualifying. After you add it, confirm each element with a named check, then qualify it.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('verify.registry.f_name', 'Organization name')} className="sm:col-span-2">
            <input className={inputClass} value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>

          <Field
            label={t('verify.registry.f_host', 'Is it on WeaveIn?')}
            hint={t(
              'verify.registry.f_host_hint',
              'Link it to its WeaveIn organization so its own staff record hours there. Leave blank for an organization that is not on WeaveIn.',
            )}
            className="sm:col-span-2"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                className={inputClass}
                placeholder={t('verify.registry.f_host_search', 'Search organizations…')}
                value={orgQuery}
                onChange={(e) => setOrgQuery(e.target.value)}
              />
              <NativeSelect
                value={form.host_cbo_id}
                onChange={(e) => {
                  set('host_cbo_id', e.target.value);
                  const picked = orgs.find((o) => String(o.id) === e.target.value);
                  if (picked && !form.name.trim()) set('name', picked.name);
                }}
              >
                <option value="">{t('verify.registry.f_host_none', 'Not on WeaveIn')}</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </Field>

          <Field label={t('verify.registry.f_street', 'Street address')} className="sm:col-span-2">
            <input className={inputClass} value={form.street_address} onChange={(e) => set('street_address', e.target.value)} />
          </Field>
          <Field label={t('verify.registry.f_city', 'City')}>
            <input className={inputClass} value={form.city} onChange={(e) => set('city', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('verify.registry.f_state', 'State')}>
              <input
                className={inputClass}
                maxLength={2}
                value={form.state}
                onChange={(e) => set('state', e.target.value.toUpperCase())}
              />
            </Field>
            <Field label={t('verify.registry.f_zip', 'ZIP')}>
              <input className={inputClass} value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} />
            </Field>
          </div>

          <Field label={t('verify.registry.f_contact', 'Point of contact (named person)')}>
            <input className={inputClass} value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} />
          </Field>
          <Field label={t('verify.registry.f_contact_phone', 'Contact phone')}>
            <input className={inputClass} value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} />
          </Field>
          <Field label={t('verify.registry.f_contact_email', 'Contact email')} className="sm:col-span-2">
            <input
              className={inputClass}
              type="email"
              value={form.contact_email}
              onChange={(e) => set('contact_email', e.target.value)}
            />
          </Field>

          <Field label={t('verify.registry.f_basis', 'Qualifying basis')}>
            <NativeSelect value={form.qualifying_basis} onChange={(e) => set('qualifying_basis', e.target.value)}>
              <option value="">{t('verify.choose', 'Choose…')}</option>
              {QUALIFYING_BASES.map((b) => (
                <option key={b} value={b}>
                  {humanize(b)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label={t('verify.registry.f_oversight', 'Oversight method')}>
            <NativeSelect value={form.oversight_method} onChange={(e) => set('oversight_method', e.target.value)}>
              <option value="">{t('verify.choose', 'Choose…')}</option>
              {OVERSIGHT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {humanize(m)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field
            label={t('verify.registry.f_evidence', 'Supporting evidence')}
            hint={t('verify.registry.f_evidence_hint', 'For example: IRS determination letter, EIN, program description.')}
            className="sm:col-span-2"
          >
            <textarea
              className={`${inputClass} min-h-[72px]`}
              value={form.qualifying_evidence}
              onChange={(e) => set('qualifying_evidence', e.target.value)}
            />
          </Field>

          <Field label={t('verify.registry.f_activities', 'Qualifying activity types')} className="sm:col-span-2">
            <div className="flex flex-wrap gap-2">
              {ACTIVITY_TYPES.map((a) => {
                const on = form.activity_types.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleType(a)}
                    className={`rounded-full border px-3 py-1 text-sm ${
                      on ? 'border-v2accent bg-v2accent-soft text-v2accent-ink' : 'border-v2border text-ink-700'
                    }`}
                  >
                    {activityLabel(t, a)}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={t('verify.registry.f_discovered', 'How did we find it?')} className="sm:col-span-2">
            <NativeSelect value={form.discovered_via} onChange={(e) => set('discovered_via', e.target.value)}>
              {DISCOVERY.map((d) => (
                <option key={d} value={d}>
                  {humanize(d)}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>

        <ErrorNote message={error} />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('verify.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={saving || form.name.trim().length < 2}>
            {saving ? t('verify.saving', 'Saving…') : t('verify.registry.add', 'Add organization')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Site detail: the five elements, qualify, and the QR door
// ---------------------------------------------------------------------------

function elementValue(site: Site, el: SiteElement, t: (k: string, f: string) => string): string {
  switch (el) {
    case 'qualifying_basis':
      return humanize(site.qualifying_basis);
    case 'evidence':
      return site.qualifying_evidence || '—';
    case 'oversight_method':
      return humanize(site.oversight_method);
    case 'point_of_contact':
      return [site.contact_name, site.contact_phone, site.contact_email].filter(Boolean).join(' · ') || '—';
    case 'activity_types':
      return site.activity_types.map((a) => activityLabel(t, a)).join(', ') || '—';
    default:
      return '—';
  }
}

function SiteDetail({ site, onChange }: { site: Site; onChange: (s: Site) => void }) {
  const { t } = useAppTranslation();
  const [checking, setChecking] = useState<SiteElement | null>(null);
  const [criteria, setCriteria] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const checksBy = useMemo(() => {
    const m: Record<string, Site['checks']> = {};
    site.checks.forEach((c) => {
      (m[c.element] ||= []).push(c);
    });
    return m;
  }, [site.checks]);

  const run = async (fn: () => Promise<Site>) => {
    setBusy(true);
    setError(null);
    setMissing([]);
    try {
      onChange(await fn());
      return true;
    } catch (e) {
      if (e instanceof VerifyApiError && e.missing.length) setMissing(e.missing);
      setError(errMessage(e, t('verify.registry.action_failed', 'That did not work.')));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveCheck = async () => {
    if (!checking) return;
    const ok = await run(() =>
      verifyApi.addCheck(site.id, {
        element: checking,
        criteria_ref: criteria.trim() || undefined,
        notes: notes.trim() || undefined,
      }),
    );
    if (ok) {
      setChecking(null);
      setCriteria('');
      setNotes('');
    }
  };

  const copyLink = async () => {
    if (!site.checkin_url) return;
    try {
      await navigator.clipboard.writeText(site.checkin_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked; the link is visible to copy by hand
    }
  };

  return (
    <div className="space-y-5">
      <SheetHeader>
        <SheetTitle>{site.name}</SheetTitle>
        <SheetDescription>
          {[site.street_address, site.city, site.state, site.zip_code].filter(Boolean).join(', ') ||
            t('verify.registry.no_address', 'No address on file')}
        </SheetDescription>
      </SheetHeader>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <SiteStatusBadge status={site.status} t={t} />
        {site.host_org_name && (
          <span className="text-ink-500">
            {t('verify.registry.hosted_by', 'On WeaveIn as {{name}}', { name: site.host_org_name })}
          </span>
        )}
        <span className="text-ink-500">
          · {t('verify.registry.found_via', 'Found via {{how}}', { how: humanize(site.discovered_via).toLowerCase() })}
        </span>
        {site.qualified_at && (
          <span className="text-ink-500">
            · {t('verify.registry.qualified_on', 'Qualified {{date}}', { date: fmtDate(site.qualified_at) })}
          </span>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-ink">{t('verify.registry.elements', 'Qualification elements')}</h3>
        {SITE_ELEMENTS.map((el) => {
          const checks = checksBy[el] || [];
          const done = checks.length > 0;
          const last = checks[checks.length - 1];
          return (
            <div key={el} className="rounded-md border border-v2border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  {done ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-success" />
                  ) : (
                    <Circle className="mt-0.5 h-4 w-4 text-ink-300" />
                  )}
                  <div>
                    <div className="text-sm font-medium text-ink">{elementLabel(t, el)}</div>
                    <div className="text-sm text-ink-700">{elementValue(site, el, t)}</div>
                    {last && (
                      <div className="mt-1 text-xs text-ink-500">
                        {t('verify.registry.checked_by', 'Checked by {{who}} ({{org}}) on {{date}}', {
                          who: last.checked_by_name || t('verify.unknown', 'unknown'),
                          org: last.checked_by_org || '—',
                          date: fmtDate(last.checked_at),
                        })}
                        {last.criteria_ref ? ` · ${last.criteria_ref}` : ''}
                        {last.notes ? ` · ${last.notes}` : ''}
                      </div>
                    )}
                  </div>
                </div>
                {site.can_manage && checking !== el && (
                  <Button size="sm" variant="outline" onClick={() => setChecking(el)} disabled={busy}>
                    {done ? t('verify.registry.recheck', 'Check again') : t('verify.registry.confirm', 'Confirm')}
                  </Button>
                )}
              </div>
              {checking === el && (
                <div className="mt-3 space-y-2 border-t border-v2border pt-3">
                  <Field label={t('verify.registry.criteria', 'Criteria checked against')}>
                    <input
                      className={inputClass}
                      placeholder={t('verify.registry.criteria_ph', 'e.g. State volunteer-organization criteria, draft MVP form')}
                      value={criteria}
                      onChange={(e) => setCriteria(e.target.value)}
                    />
                  </Field>
                  <Field label={t('verify.registry.notes', 'Notes')}>
                    <input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </Field>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setChecking(null)}>
                      {t('verify.cancel', 'Cancel')}
                    </Button>
                    <Button size="sm" onClick={() => void saveCheck()} disabled={busy}>
                      {t('verify.registry.record_check', 'Record check')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ErrorNote message={error} />
      {missing.length > 0 && (
        <div className="text-sm text-ink-700">
          {t('verify.registry.still_missing', 'Still needs a check:')}{' '}
          {missing.map((m) => elementLabel(t, m)).join(', ')}
        </div>
      )}

      {site.can_manage && (
        <div className="flex flex-wrap gap-2">
          {site.status !== 'qualified' && (
            <Button onClick={() => void run(() => verifyApi.setSiteStatus(site.id, 'qualified'))} disabled={busy}>
              {t('verify.registry.qualify', 'Qualify organization')}
            </Button>
          )}
          {site.status === 'qualified' && (
            <Button variant="outline" onClick={() => void run(() => verifyApi.setSiteStatus(site.id, 'suspended'))} disabled={busy}>
              {t('verify.registry.suspend', 'Suspend')}
            </Button>
          )}
          {site.status === 'suspended' && (
            <Button variant="outline" onClick={() => void run(() => verifyApi.setSiteStatus(site.id, 'in_review'))} disabled={busy}>
              {t('verify.registry.reopen', 'Back to review')}
            </Button>
          )}
        </div>
      )}

      {site.checkin_url && (
        <div className="space-y-3 rounded-md border border-v2border p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink">
            <QrCode className="h-4 w-4" />
            {t('verify.registry.qr_title', 'QR self check-in')}
          </div>
          <p className="text-sm text-ink-500">
            {site.status === 'qualified'
              ? t(
                  'verify.registry.qr_body',
                  'Members scan this at the door and record their hours. Each check-in waits for your staff to confirm it in Records.',
                )
              : t('verify.registry.qr_inactive', 'The code starts working once the organization is qualified.')}
          </p>
          {site.checkin_qr && (
            <img src={site.checkin_qr} alt="" className="h-40 w-40 rounded border border-v2border bg-white p-2" />
          )}
          <div className="break-all rounded bg-paper-2 px-2 py-1 font-mono text-xs text-ink-700">{site.checkin_url}</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => void copyLink()}>
              <Copy className="mr-2 h-4 w-4" />
              {copied ? t('verify.copied', 'Copied') : t('verify.copy_link', 'Copy link')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.open(site.checkin_url, '_blank', 'noopener')}>
              <ExternalLink className="mr-2 h-4 w-4" />
              {t('verify.registry.open_checkin', 'Open check-in page')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => printPoster(site, t)}>
              <Printer className="mr-2 h-4 w-4" />
              {t('verify.registry.print_poster', 'Print poster')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/** A one-page bilingual poster: the organization, the code (when the API sends one), and the link. */
function printPoster(site: Site, t: (k: string, f: string) => string) {
  // No 'noopener' here: with it, window.open returns null and there is nothing to write into.
  const w = window.open('', '_blank', 'width=800,height=1000');
  if (!w) return;
  const qr = site.checkin_qr
    ? `<img src="${site.checkin_qr}" style="width:320px;height:320px;image-rendering:pixelated" alt="" />`
    : '';
  const activities = site.activity_types.map((a) => escapeHtml(activityLabel(t, a))).join(' · ');
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(site.name)}</title>
<style>body{font-family:Inter,system-ui,sans-serif;color:#14211f;text-align:center;padding:48px}
h1{font-size:34px;margin:0 0 8px}h2{font-size:22px;font-weight:500;margin:4px 0 24px;color:#3d4b49}
.url{font-family:ui-monospace,monospace;font-size:14px;word-break:break-all;margin-top:16px}
.es{margin-top:28px;color:#3d4b49}.foot{margin-top:40px;font-size:12px;color:#6b7775}</style></head><body>
<h1>${escapeHtml(t('verify.poster.title', 'Record your hours here'))}</h1>
<h2>${escapeHtml(site.name)}</h2>
${qr}
<div class="url">${escapeHtml(site.checkin_url || '')}</div>
<p>${escapeHtml(t('verify.poster.body', 'Scan, enter your name and the hours you did today. Staff confirm it, and it counts toward your Medicaid work requirement.'))}</p>
<p class="es">Registre sus horas aquí. Escanee, escriba su nombre y las horas de hoy. El personal las confirma y cuentan para el requisito de trabajo de Medicaid.</p>
<p>${activities}</p>
<div class="foot">${escapeHtml(t('verify.poster.privacy', 'We never ask for your Social Security number.'))} · No pedimos su número de Seguro Social.</div>
<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
</body></html>`);
  w.document.close();
}
